import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import Swal from 'sweetalert2';
import { suppliesService } from '../../services/supplies';
import { productsService } from '../../services/products';
import type { Branch } from '../../types/supplies';
import type { ProductSearchResult } from '../../types/products';
import 'react-datepicker/dist/react-datepicker.css';

interface OrderItem {
  id: string;
  item_code: number | string; // Allow both number and string for item_code during editing
  item_name: string;
  quantity: number;
  item_price: number;
  total_price: number;
  prod_class?: string;
  notes?: string;
  photos?: string[];
  searchResults?: ProductSearchResult[];
  showSearchResults?: boolean;
}

const NewSupplyOrder: React.FC = () => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [productClasses, setProductClasses] = useState<string[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<number | ''>('');
  const [orderDate, setOrderDate] = useState<Date>(new Date());
  const [callCenter, setCallCenter] = useState<boolean>(false);
  const [socialMedia, setSocialMedia] = useState<boolean>(false);
  const [items, setItems] = useState<OrderItem[]>([
    {
      id: '1',
      item_code: '', // Start with empty string instead of 0
      item_name: '',
      quantity: 1,
      item_price: 0,
      total_price: 0,
      prod_class: '',
      notes: '',
      photos: []
    }
  ]);
  const [customProductClass, setCustomProductClass] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const itemNameRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  useEffect(() => {
    loadInitialData();
  }, []);

  // Click outside handler to close search dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.search-container')) {
        setItems(prev => prev.map(item => ({ ...item, showSearchResults: false })));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadInitialData = async () => {
    try {
      const [branchesData, classesData] = await Promise.all([
        suppliesService.getBranches(),
        productsService.getProductClasses()
      ]);
      setBranches(branchesData);
      setProductClasses(classesData);
    } catch (error) {
      console.error('Error loading initial data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load initial data. Please refresh the page.',
        confirmButtonColor: '#ff6b35'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleItemCodeChange = async (itemId: string, code: string) => {
    // Find the current item index
    const currentItemIndex = items.findIndex(item => item.id === itemId);
    
    // Prevent entering "0" in items after the first item if first item has valid code
    if (currentItemIndex > 0 && firstItemHasValidCode && code === '0') {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Item Code',
        text: 'Cannot use item code "0" when the first item has a valid item code.',
        confirmButtonColor: '#ff6b35'
      });
      return; // Don't update the item code
    }

    // Update item code first (keep as string during editing)
    setItems(prev => prev.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          item_code: code,
          showSearchResults: false
        };
      }
      return item;
    }));

    // Real-time lookup if code is a valid number and not empty
    const numericCode = parseInt(code);
    if (code && !isNaN(numericCode) && numericCode > 0) {
      try {
        const product = await productsService.getProductByCode(numericCode);
        setItems(prev => prev.map(item => {
          if (item.id === itemId) {
            const updatedItem = {
              ...item,
              item_name: product.item_name,
              item_price: product.item_price,
              prod_class: product.prod_class || ''
            };
            updatedItem.total_price = updatedItem.quantity * updatedItem.item_price;
            return updatedItem;
          }
          return item;
        }));
      } catch (error: any) {
        // Silent fail for other errors - just clear the name if product not found
        setItems(prev => prev.map(item => {
          if (item.id === itemId) {
            return { ...item, item_name: '', item_price: 0, total_price: 0 };
          }
          return item;
        }));
      }
    } else if (!code) {
      // Clear name and price if code is empty
      setItems(prev => prev.map(item => {
        if (item.id === itemId) {
          return { ...item, item_name: '', item_price: 0, total_price: 0 };
        }
        return item;
      }));
    }
  };

  const handleItemNameSearch = async (itemId: string, searchTerm: string) => {
    // Update the item name and show search results
    setItems(prev => prev.map(i => 
      i.id === itemId 
        ? { ...i, item_name: searchTerm, showSearchResults: searchTerm.length >= 1 }
        : i
    ));

    // Search for products if user has typed at least 1 character
    if (searchTerm.length >= 1) {
      try {
        const results = await productsService.searchProducts(searchTerm, 1000); // Increased limit to get more results
        setItems(prev => prev.map(i => 
          i.id === itemId 
            ? { ...i, searchResults: results }
            : i
        ));
      } catch (error) {
        console.error('Error searching products:', error);
        // Clear search results on error
        setItems(prev => prev.map(i => 
          i.id === itemId 
            ? { ...i, searchResults: [], showSearchResults: false }
            : i
        ));
      }
    } else {
      // Clear search results when search term is empty
      setItems(prev => prev.map(i => 
        i.id === itemId 
          ? { ...i, searchResults: [], showSearchResults: false }
          : i
      ));
    }
  };

  const handleProductSelect = (itemId: string, product: ProductSearchResult) => {
    setItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const updatedItem = {
          ...item,
          item_code: product.item_code,
          item_name: product.item_name,
          item_price: product.item_price,
          prod_class: product.prod_class || '',
          showSearchResults: false,
          searchResults: []
        };
        updatedItem.total_price = updatedItem.quantity * updatedItem.item_price;
        return updatedItem;
      }
      return item;
    }));
  };

  const handleQuantityChange = (itemId: string, quantity: string) => {
    const numericQuantity = parseInt(quantity) || 1;
    setItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, quantity: numericQuantity };
        updatedItem.total_price = updatedItem.quantity * updatedItem.item_price;
        return updatedItem;
      }
      return item;
    }));
  };

  const handlePriceChange = (itemId: string, price: string) => {
    const numericPrice = parseFloat(price) || 0;
    setItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, item_price: numericPrice };
        updatedItem.total_price = updatedItem.quantity * updatedItem.item_price;
        return updatedItem;
      }
      return item;
    }));
  };

  const handleProductClassChange = (itemId: string, prodClass: string) => {
    setItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, prod_class: prodClass } : item
    ));
  };

  const handleNotesChange = (itemId: string, notes: string) => {
    setItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, notes } : item
    ));
  };

  const addNewItem = () => {
    const newItem: OrderItem = {
      id: Date.now().toString(),
      item_code: '', // Start with empty item code
      item_name: '',
      quantity: 1,
      item_price: 0,
      total_price: 0,
      prod_class: '',
      notes: '',
      photos: []
    };
    setItems(prev => [...prev, newItem]);
  };

  const removeItem = (itemId: string) => {
    if (items.length === 1) return;
    setItems(prev => prev.filter(item => item.id !== itemId));
  };

  // Check if any item has item_code of 0
  const hasZeroItemCode = items.some(item => {
    const numericCode = typeof item.item_code === 'string' ? parseInt(item.item_code) : item.item_code;
    return numericCode === 0;
  });

  // Check if the first item has a valid non-zero item code
  const firstItemHasValidCode = items.length > 0 && items[0].item_code !== '' && items[0].item_code !== '0' && items[0].item_code !== 0;

  // Function to check if an item code input should be restricted
  const shouldRestrictItemCode = (currentItemIndex: number) => {
    return currentItemIndex > 0 && firstItemHasValidCode;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedBranch) {
      Swal.fire({
        icon: 'warning',
        title: 'Branch Required',
        text: 'Please select a branch.',
        confirmButtonColor: '#ff6b35'
      });
      return;
    }

    const hasEmptyItems = items.some(item => !item.item_name || item.quantity <= 0);
    if (hasEmptyItems) {
      Swal.fire({
        icon: 'warning',
        title: 'Incomplete Items',
        text: 'Please fill in all item details.',
        confirmButtonColor: '#ff6b35'
      });
      return;
    }

    setIsSubmitting(true);
    try {
    // call_center codes: 0 = Branch, 1 = Call Center, 2 = Social Media
    const callCenterCode = socialMedia ? 2 : (callCenter ? 1 : 0);

    const orderData = {
    branch_id: selectedBranch as number,
    order_date: orderDate.toISOString(),
    call_center: callCenterCode,
      items: items.map(item => ({
        item_code: typeof item.item_code === 'string' ? parseInt(item.item_code) || 0 : item.item_code,
        item_name: item.item_name,
        quantity: item.quantity,
        item_price: item.item_price,
        prod_class: item.prod_class === 'other' ? customProductClass : item.prod_class,
        notes: item.notes,
        photos: item.photos
      }))
    };
    
    await suppliesService.createSupplyOrder(orderData);
      
      Swal.fire({
        icon: 'success',
        title: 'Order Created',
        text: 'Supply order has been created successfully!',
        confirmButtonColor: '#ff6b35'
      });

      // Reset form
      setSelectedBranch('');
      setOrderDate(new Date());
      setCallCenter(false);
      setSocialMedia(false);
      setItems([{
        id: '1',
        item_code: '', // Reset to empty string
        item_name: '',
        quantity: 1,
        item_price: 0,
        total_price: 0,
        prod_class: '',
        notes: '',
        photos: []
      }]);
      setCustomProductClass('');
    } catch (error) {
      console.error('Error creating order:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to create supply order. Please try again.',
        confirmButtonColor: '#ff6b35'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-orange" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="text-muted mt-2">Loading form data...</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">
          <i className="bi bi-plus-circle me-2"></i>
          New Supply Order
        </h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {/* Branch and Date */}
          <div className="row mb-4">
            <div className="col-md-6">
              <label htmlFor="branch" className="form-label">
                Branch <span className="text-danger">*</span>
              </label>
              <select
                id="branch"
                className="form-select"
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value ? parseInt(e.target.value) : '')}
                required
              >
                <option value="">Select Branch</option>
                {branches.map(branch => (
                  <option key={branch.branch_id} value={branch.branch_id}>
                    {branch.branch}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">
                Date <span className="text-danger">*</span>
              </label>
              <div>
                <DatePicker
                  selected={orderDate}
                  onChange={(date) => setOrderDate(date || new Date())}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  required
                />
              </div>
            </div>
          </div>

          {/* Call Center Checkbox */}
          <div className="row mb-4">
            <div className="col-md-12">
              <div className="d-flex align-items-center">
                <div className="form-check me-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="callCenter"
                    checked={callCenter}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setCallCenter(checked);
                      if (checked) setSocialMedia(false);
                    }}
                  />
                  <label className="form-check-label" htmlFor="callCenter">
                    Call Center
                  </label>
                </div>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="socialmedia"
                    checked={socialMedia}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSocialMedia(checked);
                      if (checked) setCallCenter(false);
                    }}
                  />
                  <label className="form-check-label" htmlFor="socialmedia">
                    Social Media
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Items */}
          {items.map((item, index) => (
            <div key={item.id} className="item-row border rounded p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Item {index + 1}</h6>
                {items.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeItem(item.id)}
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                )}
              </div>

              <div className="row">
                {/* Item Code */}
                <div className="col-md-3 mb-3">
                  <label className="form-label">Item Code</label>
                  <input
                    type="text"
                    className={`form-control ${shouldRestrictItemCode(index) ? 'border-warning' : ''}`}
                    value={item.item_code}
                    onChange={(e) => handleItemCodeChange(item.id, e.target.value)}
                    placeholder={shouldRestrictItemCode(index) ? "Cannot use '0'" : "Enter item code"}
                    title={shouldRestrictItemCode(index) ? "Cannot use item code '0' when first item has valid code" : ""}
                  />
                  {shouldRestrictItemCode(index) && (
                    <small className="text-warning">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      Cannot use "0" as item code
                    </small>
                  )}
                </div>

                {/* Item Name */}
                <div className="col-md-3 mb-3">
                  <label className="form-label">Item Name</label>
                  <div className="position-relative search-container">
                    <input
                      ref={el => itemNameRefs.current[item.id] = el}
                      type="text"
                      className="form-control"
                      value={item.item_name}
                      onChange={(e) => handleItemNameSearch(item.id, e.target.value)}
                      placeholder="Type to search or enter name"
                    />
                    {item.showSearchResults && item.searchResults && item.searchResults.length > 0 && (
                      <div className="search-results">
                        {item.searchResults.map((result) => (
                          <div
                            key={result.item_code}
                            className="search-result-item"
                            onClick={() => handleProductSelect(item.id, result)}
                          >
                            <strong>{result.item_code}</strong> - {result.item_name}
                            <small className="text-muted d-block">${result.item_price}</small>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Quantity */}
                <div className="col-md-2 mb-3">
                  <label className="form-label">Quantity</label>
                  <input
                    type="number"
                    className="form-control"
                    value={item.quantity}
                    onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                    min="1"
                  />
                </div>

                {/* Item Price */}
                <div className="col-md-2 mb-3">
                  <label className="form-label">Item Price</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={item.item_price}
                    onChange={(e) => handlePriceChange(item.id, e.target.value)}
                  />
                </div>

                {/* Total Price */}
                <div className="col-md-2 mb-3">
                  <label className="form-label">Total Price</label>
                  <input
                    type="text"
                    className="form-control"
                    value={typeof item.total_price === 'number' ? item.total_price.toFixed(2) : '0.00'}
                    readOnly
                  />
                </div>

                {/* Product Class */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Product Class</label>
                  <select
                    className="form-select"
                    value={item.prod_class}
                    onChange={(e) => handleProductClassChange(item.id, e.target.value)}
                  >
                    <option value="">Select Product Class</option>
                    {productClasses.map(cls => (
                      <option key={cls} value={cls}>{cls}</option>
                    ))}
                    <option value="other">Other (Custom)</option>
                  </select>
                  {item.prod_class === 'other' && (
                    <input
                      type="text"
                      className="form-control mt-2"
                      placeholder="Enter custom product class"
                      value={customProductClass}
                      onChange={(e) => setCustomProductClass(e.target.value)}
                    />
                  )}
                </div>

                {/* Notes */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={item.notes}
                    onChange={(e) => handleNotesChange(item.id, e.target.value)}
                    placeholder="Additional notes"
                  />
                </div>

                {/* Photos */}
                <div className="col-12 mb-3">
                  <label className="form-label">Photos</label>
                  
                  {/* Photo upload section - merged directly */}
                  <div>
                    {/* Photo dropzone exactly like SupplyOrders.tsx */}
                    <div className="photo-dropzone" onClick={() => {
                      const fileInput = document.getElementById(`photo-input-${item.id}`) as HTMLInputElement;
                      if (fileInput) fileInput.click();
                    }}>
                      <input
                        id={`photo-input-${item.id}`}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(event) => {
                          const files = event.target.files;
                          if (!files || files.length === 0) return;
                          const validFiles: File[] = [];
                          
                          // First, validate all files and collect valid ones
                          Array.from(files).forEach(file => {
                            // Validate file type
                            if (!file.type.startsWith('image/')) {
                              Swal.fire({
                                icon: 'warning',
                                title: 'Invalid File Type',
                                text: `File "${file.name}" is not a valid image file.`,
                                confirmButtonColor: '#ff6b35'
                              });
                              return;
                            }

                            // Validate file size (10MB limit)
                            if (file.size > 10485760) {
                              Swal.fire({
                                icon: 'warning',
                                title: 'File Too Large',
                                text: `File "${file.name}" is larger than 10MB limit.`,
                                confirmButtonColor: '#ff6b35'
                              });
                              return;
                            }

                            validFiles.push(file);
                          });

                          if (validFiles.length === 0) {
                            return;
                          }

                          const photosToAdd: string[] = [];
                          let filesProcessed = 0;
                          let imagesLoaded = 0;
                          const totalImages = validFiles.length;

                          // Process valid files
                          validFiles.forEach((file) => {
                            const reader = new FileReader();
                            reader.onload = (e) => {
                              const base64 = e.target?.result as string;
                              
                              if (base64) {
                                // Create an image to get dimensions and compress if needed
                                const img = new Image();
                                img.onload = () => {
                                  const canvas = document.createElement('canvas');
                                  const ctx = canvas.getContext('2d');
                                  
                                  // Set maximum dimensions (compress large images)
                                  const MAX_WIDTH = 800;
                                  const MAX_HEIGHT = 600;
                                  let { width, height } = img;
                                  
                                  // Calculate new dimensions
                                  if (width > height) {
                                    if (width > MAX_WIDTH) {
                                      height = height * (MAX_WIDTH / width);
                                      width = MAX_WIDTH;
                                    }
                                  } else {
                                    if (height > MAX_HEIGHT) {
                                      width = width * (MAX_HEIGHT / height);
                                      height = MAX_HEIGHT;
                                    }
                                  }
                                  
                                  // Set canvas dimensions and draw compressed image
                                  canvas.width = width;
                                  canvas.height = height;
                                  ctx?.drawImage(img, 0, 0, width, height);
                                  
                                  // Get compressed base64 (with reduced quality)
                                  const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
                                  photosToAdd.push(compressedBase64);
                                  
                                  // Track images that have been fully loaded and processed
                                  imagesLoaded++;
                                  
                                  // When all images are fully processed, update the state
                                  if (imagesLoaded === totalImages) {
                                    setItems(prev => prev.map(prevItem => 
                                      prevItem.id === item.id 
                                        ? { ...prevItem, photos: [...(prevItem.photos || []), ...photosToAdd] }
                                        : prevItem
                                    ));
                                  }
                                };
                                img.src = base64;
                              } else {
                                // If base64 is empty, still track it
                                imagesLoaded++;
                              }
                              filesProcessed++;
                            };
                            
                            reader.onerror = (error) => {
                              console.error('Error reading file:', file.name, error);
                              filesProcessed++;
                              imagesLoaded++;
                              
                              // Still check if all images are processed even on error
                              if (imagesLoaded === totalImages && photosToAdd.length > 0) {
                                setItems(prev => prev.map(prevItem => 
                                  prevItem.id === item.id 
                                    ? { ...prevItem, photos: [...(prevItem.photos || []), ...photosToAdd] }
                                    : prevItem
                                ));
                              }
                            };
                            
                            reader.readAsDataURL(file);
                          });

                          // Reset the input value
                          const fileInput = event.target as HTMLInputElement;
                          fileInput.value = '';
                        }}
                        className="photo-input-hidden"
                      />
                      <i className="bi bi-cloud-upload"></i>
                      <p className="mb-0">Click to select photos</p>
                    </div>

                    {/* Clear all photos button */}
                    {item.photos && item.photos.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm mt-2"
                        onClick={() => {
                          setItems(prev => prev.map(prevItem => 
                            prevItem.id === item.id 
                              ? { ...prevItem, photos: [] }
                              : prevItem
                          ));
                        }}
                      >
                        Clear All Photos ({item.photos.length})
                      </button>
                    )}

                    {/* Photo Previews */}
                    {item.photos && item.photos.length > 0 && (
                      <div className="mt-3">
                        <small className="text-muted d-block mb-2">
                          <strong>{item.photos.length}</strong> photo{item.photos.length !== 1 ? 's' : ''} uploaded
                        </small>
                        <div className="photo-preview-grid">
                          {item.photos.map((photo, photoIndex) => (
                            <div key={photoIndex} className="photo-preview-item">
                              <img 
                                src={photo} 
                                alt={`Preview ${photoIndex + 1}`}
                                onClick={() => window.open(photo, '_blank')}
                                title="Click to view full size"
                              />
                              <button
                                type="button"
                                className="btn-remove-photo"
                                onClick={() => {
                                  setItems(prev => prev.map(prevItem => 
                                    prevItem.id === item.id 
                                      ? { ...prevItem, photos: prevItem.photos?.filter((_, i) => i !== photoIndex) }
                                      : prevItem
                                  ));
                                }}
                                title="Remove photo"
                              >
                                <i className="bi bi-x"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>
          ))}

          {/* Add Item Button */}
          <div className="row mb-4">
            <div className="col">
              <button
                type="button"
                className={`btn ${hasZeroItemCode ? 'btn-secondary' : 'btn-outline-primary'}`}
                onClick={addNewItem}
                disabled={hasZeroItemCode}
                title={hasZeroItemCode ? 'Cannot add items when any item code is 0' : 'Add Another Item'}
              >
                <i className="bi bi-plus me-2"></i>
                Add Another Item
              </button>
              {hasZeroItemCode && (
                <small className="text-muted d-block mt-1">
                  <i className="bi bi-info-circle me-1"></i>
                  Cannot add new items when any item has code 0
                </small>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="row">
            <div className="col">
              <button
                type="submit"
                className="btn btn-orange"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Creating Order...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    Create Supply Order
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewSupplyOrder;
